<template>
    <div class="configure-practice-pulse">
        <!-- Modern Header -->
        <div class="config-header">
            <div class="header-content">
                <h1 class="header-title">Configure Practice Ops</h1>
            </div>
            <div class="header-actions">
                <b-button variant="success" class="btn-save" @click="save">
                    <i class="fa fa-check mr-1"></i> Save
                </b-button>
                <b-button variant="outline-secondary" class="btn-cancel" @click="doCancel">
                    Cancel
                </b-button>
                <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
                    <i class="la la-times"></i>
                </button>
            </div>
        </div>

        <PopupErrorDisplay/>

        <b-overlay :show="overlayshow" rounded="sm">
            <div class="config-content" v-if="practicePulseSettings">
                <!-- Status Settings Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-sliders section-icon"></i>
                            Status Settings
                        </h3>
                    </div>
                    <div class="section-body">
                        <div class="form-grid">
                            <div class="form-field">
                                <label class="field-label">Patient Arrived Status</label>
                                <multiselect
                                    v-if="statuses"
                                    v-model="practicePulseSettings.arrivedStatus"
                                    :options="statuses"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="true"
                                    label="ItemName"
                                    track-by="DefNum"
                                    placeholder="Select Status"
                                />
                            </div>

                            <div class="form-field">
                                <label class="field-label">Ready to go back or Ready for Treatment Status</label>
                                <multiselect
                                    v-if="statuses"
                                    v-model="practicePulseSettings.readyStatus"
                                    :options="statuses"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="ItemName"
                                    track-by="ItemName"
                                    placeholder="Select Status"
                                />
                            </div>

                            <div class="form-field">
                                <label class="field-label">Dismissed Status</label>
                                <multiselect
                                    v-if="statuses"
                                    v-model="practicePulseSettings.dismissedStatus"
                                    :options="statuses"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="ItemName"
                                    track-by="ItemName"
                                    placeholder="Select Status"
                                />
                            </div>

                            <div class="form-field">
                                <label class="field-label">In Treatment Room Status</label>
                                <multiselect
                                    v-if="statuses"
                                    v-model="practicePulseSettings.inTreatmentRoomStatus"
                                    :options="statuses"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="ItemName"
                                    track-by="ItemName"
                                    placeholder="Select Status"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Visual Alerts Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-bell section-icon"></i>
                            Visual Alerts
                        </h3>
                    </div>
                    <div class="section-body">
                        <div class="checkbox-list">
                            <label class="checkbox-item">
                                <b-form-checkbox
                                    v-model="practicePulseSettings.patientBalanceAlert"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                />
                                <span class="checkbox-label">Show visual color indicator when there's a patient balance on account</span>
                            </label>

                            <label class="checkbox-item">
                                <b-form-checkbox
                                    v-model="practicePulseSettings.patientFamilyOverdueRecallAlert"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                />
                                <span class="checkbox-label">Show visual alert if patient family member(s) is overdue for recall</span>
                            </label>

                            <div class="checkbox-item-complex">
                                <label class="checkbox-item">
                                    <b-form-checkbox
                                        v-model="practicePulseSettings.elapsedTimeAlert"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />
                                    <span class="checkbox-label">Show visual alert if more than</span>
                                </label>
                                <div class="checkbox-sub-content">
                                    <b-form-input
                                        class="elapsed-input"
                                        type="number"
                                        v-model.number="practicePulseSettings.elapsedTime"
                                        :disabled="practicePulseSettings.elapsedTimeAlert !== 'TRUE'"
                                    />
                                    <span class="checkbox-label-continued">minutes elapsed between the time Patient marked ready for treatment and the time patient is moved to the operatory</span>
                                </div>
                            </div>

                            <label class="checkbox-item">
                                <b-form-checkbox
                                    v-model="practicePulseSettings.showOrthoAlert"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                />
                                <span class="checkbox-label">Show alert if patient has ortho benefits attached to their insurance on Open Dental</span>
                            </label>

                            <label class="checkbox-item">
                                <b-form-checkbox
                                    v-model="practicePulseSettings.showTasks"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                />
                                <span class="checkbox-label">Show Patient associated Teamio Tasks</span>
                            </label>

                            <label class="checkbox-item">
                                <b-form-checkbox
                                    v-model="practicePulseSettings.showChat"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                />
                                <span class="checkbox-label">Show Chat</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Setup Operatories Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-hospital-o section-icon"></i>
                            Setup Operatories
                        </h3>
                        <b-button variant="success" size="sm" class="btn-add-new" @click="addOp">
                            <i class="fa fa-plus mr-1"></i> Add New
                        </b-button>
                    </div>
                    <div class="section-body">
                        <div class="data-table">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Operatory Name</th>
                                        <th class="th-actions"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(op, index) in practicePulseSettings.allOps" :key="index">
                                        <td>
                                            <b-form-input v-model="op.OpName" class="table-input">{{ op.OpName }}</b-form-input>
                                        </td>
                                        <td class="td-actions">
                                            <span v-if="op.addedFromTeamio" class="btn-delete" @click="selectedOpIndex=index; selectedOpNum=op.OperatoryNum;showModal('deleteOp')">
                                                <i class="fa fa-trash"></i>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Alert Settings For Employees Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-users section-icon"></i>
                            Alert Settings For Employees
                        </h3>
                    </div>
                    <div class="section-body">
                        <div class="data-table" v-if="practicePulseSettings.providerSettings">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Select Team Member</th>
                                        <th>Color</th>
                                        <th>Sound</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(emp, index) in activeEmployees" :key="index">
                                        <td>
                                            <span class="mobile-display">Select Team Member</span>
                                            <multiselect
                                                v-if="practicePulseSettings.providerSettings[emp.EmployeeID]"
                                                v-model="practicePulseSettings.providerSettings[emp.EmployeeID].employee"
                                                :options="activeEmployees"
                                                :multiple="false"
                                                :taggable="true"
                                                :show-labels="true"
                                                label="Name"
                                                track-by="Name"
                                                aria-placeholder="Select Employee"
                                                ref="emp"
                                                @select="setVal($event, 'emp', index)"
                                            />
                                        </td>
                                        <td>
                                            <b-form-input
                                                v-if="practicePulseSettings.providerSettings[emp.EmployeeID]"
                                                type="color"
                                                class="color-input"
                                                v-model="practicePulseSettings.providerSettings[emp.EmployeeID].color"
                                            />
                                        </td>
                                        <td>
                                            <div class="sound-field">
                                                <multiselect
                                                    v-if="practicePulseSettings.providerSettings[emp.EmployeeID]"
                                                    v-model="practicePulseSettings.providerSettings[emp.EmployeeID].sound"
                                                    :options="alertSounds"
                                                    :multiple="false"
                                                    :taggable="true"
                                                    :show-labels="true"
                                                    label="name"
                                                    track-by="name"
                                                    aria-placeholder="Select Sound"
                                                    ref="sound"
                                                    @select="setVal($event, 'sound', index)"
                                                />
                                                <button
                                                    v-if="practicePulseSettings.providerSettings[emp.EmployeeID]"
                                                    type="button"
                                                    class="btn-play-sound"
                                                    :id="`play-sound-${emp.EmployeeID}`"
                                                    @click="playSound(practicePulseSettings.providerSettings[emp.EmployeeID].sound)"
                                                    v-b-tooltip.hover
                                                    title="Play Sound"
                                                >
                                                    <i class="fa fa-play"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>       

                <!-- Custom Alerts Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-exclamation-triangle section-icon"></i>
                            Custom Alerts
                        </h3>
                        <b-button variant="success" size="sm" class="btn-add-new" @click="addAlert">
                            <i class="fa fa-plus mr-1"></i> Add New
                        </b-button>
                    </div>
                    <div class="section-body">
                        <div class="data-table" v-if="practicePulseSettings.providerSettings">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Select Team Member</th>
                                        <th>Alert Text</th>
                                        <th class="th-actions"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(customAlert, index) in practicePulseSettings.customAlerts" :key="index">
                                        <td>
                                            <multiselect
                                                v-model="practicePulseSettings.customAlerts[index].employee"
                                                :options="activeEmployees"
                                                :multiple="false"
                                                :taggable="true"
                                                :show-labels="true"
                                                label="Name"
                                                track-by="Name"
                                                aria-placeholder="Select Employee"
                                            />
                                        </td>
                                        <td>
                                            <b-form-input
                                                class="table-input"
                                                placeholder="Enter alert text"
                                                v-model="practicePulseSettings.customAlerts[index].alertText"
                                            />
                                        </td>
                                        <td class="td-actions">
                                            <span class="btn-delete" @click="selectedAlert=index;showModal('delete')">
                                                <i class="fa fa-trash"></i>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Post-op Calls Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-phone section-icon"></i>
                            Post-op Calls
                        </h3>
                    </div>
                    <div class="section-body">
                        <div class="postop-time-setting">
                            <label class="field-label">At what hour do you want the emails to go out to the providers for the patients to be called?</label>
                            <DatePicker
                                class="time-picker"
                                type="time"
                                value-type="format"
                                :format="'hh:mm A'"
                                :time-picker-options="timePickerOptions"
                                :show-second="false"
                                v-model="practicePulseSettings.sendEmailTime"
                                lang="en"
                                :placeholder="'Select Time'"
                                input-class="form-control"
                                append-to-body
                            >
                                <i class="fa fa-clock-o" slot="calendar-icon" />
                            </DatePicker>
                        </div>
                        <div class="data-table" v-if="practicePulseSettings.providerSettings">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Select Team Member</th>
                                        <th>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(emp, index) in activeEmployees" :key="index">
                                        <td>
                                            <span class="mobile-display">Select Team Member</span>
                                            <multiselect
                                                v-if="practicePulseSettings.providerSettings[emp.EmployeeID]"
                                                v-model="practicePulseSettings.providerSettings[emp.EmployeeID].employee"
                                                :options="activeEmployees"
                                                :multiple="false"
                                                :taggable="true"
                                                :show-labels="true"
                                                label="Name"
                                                track-by="Name"
                                                aria-placeholder="Select Employee"
                                                ref="emp"
                                                @select="setVal($event, 'email', index)"
                                            />
                                        </td>
                                        <td>
                                            <b-form-input
                                                v-if="practicePulseSettings.providerSettings[emp.EmployeeID]"
                                                type="email"
                                                class="table-input"
                                                placeholder="Enter email address"
                                                v-model="practicePulseSettings.providerSettings[emp.EmployeeID].email"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Quick Notes Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-sticky-note section-icon"></i>
                            Quick Notes
                        </h3>
                        <b-button variant="success" size="sm" class="btn-add-new" @click="addQuickNotes">
                            <i class="fa fa-plus mr-1"></i> Add New Category
                        </b-button>
                    </div>
                    <div class="section-body">
                        <div class="quick-notes-list">
                            <div class="quick-note-category" v-for="(value, key) in practicePulseSettings.quickNotesOptions2" :key="key">
                                <div class="category-header">
                                    <button type="button" class="btn-delete-cat" @click="delQuickNoteCat(key)">
                                        <i class="fa fa-times"></i>
                                    </button>
                                    <b-form-input
                                        class="category-title-input"
                                        placeholder="Category name"
                                        v-model="practicePulseSettings.quickNotesOptions2[key].title"
                                    />
                                    <button type="button" class="btn-add-sub" @click="addQuickNoteSubHeading(key)" v-b-tooltip.hover title="Add sub-heading">
                                        <i class="fa fa-plus"></i>
                                    </button>
                                </div>
                                <div class="category-items">
                                    <div class="category-item" v-for="(option, index) in value.val" :key="index">
                                        <button type="button" class="btn-delete-item" @click="delQuickNoteSubcat(key, index)">
                                            <i class="fa fa-times"></i>
                                        </button>
                                        <div class="item-inputs">
                                            <b-form-input
                                                class="item-name-input"
                                                placeholder="Sub-heading name"
                                                v-model="option.name"
                                            />
                                            <b-form-input
                                                class="item-notes-input"
                                                placeholder="Auto notes"
                                                v-model="practicePulseSettings.quickNotesOptions2[key].val[index].autoNotes"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>   
                        
                <!-- Proc Codes to Wiki Mapping Section -->
                <div class="config-section">
                    <div class="section-header">
                        <h3 class="section-title">
                            <i class="fa fa-book section-icon"></i>
                            Proc Codes to Wiki Mapping
                        </h3>
                    </div>
                    <div class="section-body">
                        <div class="data-table">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Select Proc Codes</th>
                                        <th>Wiki</th>
                                        <th class="th-actions"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(mapping, index) in practicePulseSettings.procCodesToWikiMappings" :key="index">
                                        <td>
                                            <span class="mobile-display">Proc Codes</span>
                                            <multiselect
                                                v-if="procCodes"
                                                v-model="mapping.procCodes"
                                                :options="procCodes"
                                                :multiple="true"
                                                :taggable="false"
                                                :show-labels="false"
                                                :custom-label="codeWithDesc"
                                                label="ProcCode"
                                                track-by="ProcCode"
                                                aria-placeholder="Add Options"
                                            >
                                                <template slot="tag" slot-scope="props">
                                                    <span class="custom__tag">
                                                        <span>{{ props.option.ProcCode }}</span>
                                                        <i tabindex="1" class="multiselect__tag-icon" @click="removeCode(index, props.option)"></i>
                                                    </span>
                                                </template>
                                                <template slot="option" slot-scope="props">
                                                    <div class="option__desc">
                                                        <div class="option__title d-flex">
                                                            <span class="option-code">{{ codeWithDesc(props.option) }}</span>
                                                        </div>
                                                    </div>
                                                </template>
                                            </multiselect>
                                        </td>
                                        <td>
                                            <span class="mobile-display">Wiki</span>
                                            <multiselect
                                                v-if="wikiPages && wikiPages.length > 0"
                                                v-model="mapping.wikiPage"
                                                :options="wikiPages"
                                                :multiple="true"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="Name"
                                                track-by="WikiPageID"
                                                aria-placeholder="Add Options"
                                            />
                                        </td>
                                        <td class="td-actions">
                                            <span class="btn-delete" @click="selectedMappingRowIndex=index;showModal('delProcCodeMapping')">
                                                <i class="fa fa-trash"></i>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <a href="#" class="btn-add-more" @click.prevent="addMoreProcCodeToWikiMapping">
                                <i class="fa fa-plus mr-1"></i> Add more
                            </a>
                        </div>
                    </div>
                </div>

                <PopupErrorDisplay/>
            </div>   
        </b-overlay>
        
        <b-modal ref="delete" id="delete" title="Delete" body-class="bg-white">
            <p>Are you sure you want to to delete this alert?</p>
            <div slot="modal-footer" class="w-100">
            <b-button variant="danger" size="sm" class="float-right" @click="selectedAlert=null; hideModal('delete')">No</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="delAlert">Yes</b-button>
            </div>
        </b-modal>

        <b-modal ref="deleteOp" id="delete" title="Delete" body-class="bg-white">
            <p>Are you sure you want to to delete this operatory?</p>
            <div slot="modal-footer" class="w-100">
            <b-button variant="danger" size="sm" class="float-right" @click="selectedAlert=null; hideModal('deleteOp')">No</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="delOp">Yes</b-button>
            </div>
        </b-modal>

        <b-modal ref="delProcCodeMapping" id="delete" title="Delete" body-class="bg-white">
            <p>Are you sure you want to to delete this mapping entry?</p>
            <div slot="modal-footer" class="w-100">
            <b-button variant="danger" size="sm" class="float-right" @click="hideModal('delProcCodeMapping')">No</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteProcCodeToWikiMapping">Yes</b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";

import mixin from '../../mixin.js';
import ppmixin from './Ppmixin.js';

export default {
    name: 'ConfigurepracticePulse',
    components: {PopupErrorDisplay, Multiselect, DatePicker},    
    props: ['practicePulseSettings', 'pp'],
    mixins: [mixin, ppmixin],

    async created() {
        //console.log(this.practicePulseSettings);
        if(this.practicePulseSettings) {
            this.origPracticePulseSettings = this.practicePulseSettings;
            await this.init();
        }
    },

    data(){
        return {
            overlayshow: false,
            origPracticePulseSettings: null,
            loadingStatus: false,
            statuses: [],
            selectedAlert: null,
            selectedOpIndex: null,
            selectedOpNum: null,
            quickNotesOptions: {
                "New Visit": [
                    {name: 'Next Recall appointment has been scheduled', checked: false, autoNotes: ''}, 
                    {name: 'Recall 3', checked: false, autoNotes: ''}, 
                    {name: 'Recall 4', checked: false, autoNotes: ''}, 
                    {name: 'Recall 5', checked: false, autoNotes: ''}
                ],
                "Invisalign": [
                    {name: 'Scan', checked: false, autoNotes: ''}, 
                    {name: 'Check and Delivery', checked: false, autoNotes: ''}, 
                    {name: 'Delivery with IPR', checked: false, autoNotes: ''}, 
                    {name: 'Remove attachment', checked: false, autoNotes: ''}, 
                    {name: 'POCheck', checked: false, autoNotes: ''}, 
                    {name: 'Cont Tx', checked: false, autoNotes: ''}, 
                    {name: 'Cementation', checked: false, autoNotes: ''}, 
                    {name: 'Delivery', checked: false, autoNotes: ''}
                ],
                "Dispense": [
                    {name: 'Sheet White', checked: false, autoNotes: ''}
                ]
            },

            quickNotesOptions2: {},

            timePickerOptions:{
                start: '16:00',
                step: '01:00',
                end: '23:00'
            },                      
            wikiPages: [],
            selectedMappingRowIndex: null,
        }
    },

    methods: {
        ...mapActions('practicepulse', ['getPracticePulseStatuses', 'savePracticePulseSettings', 'updateOps']),
        ...mapActions('wiki', ['getWikiPages']),
        ...mapActions('customer', ['getProcCodes']),

        async init() {
            this.resetError();
            //this.statuses = await this.getPracticePulseStatuses({siteID: this.siteID});
            this.overlayshow = !this.overlayshow;            
            this.resetError2();
            const p1 = this.getPracticePulseStatuses({siteID: this.siteID});
            const p2 = this.getWikiPages({siteID: this.siteID});
            const p3 = this.getProcCodes({siteID: this.siteID});
            const allPromises = Promise.all([p1,p2,p3]);

            allPromises.then(values => {
              this.overlayshow = !this.overlayshow;
              this.setLoading2(false);
              this.statuses = values[0];
              
              //this.wikiPages = values[1];
              //console.log(this.wikiPages);

              // now removing the Content property because we don't need to save that to firebase
              this.wikiPages = _.map(values[1][0], ({ Content, ...rest }) => rest);

              //console.log('this.statuses', this.statuses);
              //console.log('this.wikiPages', this.wikiPages);

              if(!this.practicePulseSettings.ops) {
                    this.practicePulseSettings.ops = this.opsWithoutCheckinCheckout;                
                }

                if(!this.practicePulseSettings.allOps || this.practicePulseSettings.allOps.length === 0) {
                    this.practicePulseSettings.allOps = this.pp.allOps;
                }

                if(this.providers && (!this.practicePulseSettings.providerSettings || this.isObjectEmpty(this.practicePulseSettings.providerSettings))) {                
                    this.practicePulseSettings.providerSettings = {}
                    for(const emp of this.activeEmployees) {
                        if(!this.practicePulseSettings.providerSettings[emp.EmployeeID]) {
                            this.practicePulseSettings.providerSettings[emp.EmployeeID] = {};
                            this.practicePulseSettings.providerSettings[emp.EmployeeID].employee= emp;
                            this.practicePulseSettings.providerSettings[emp.EmployeeID].color='';
                            this.practicePulseSettings.providerSettings[emp.EmployeeID].sound={};
                        }
                    }               
                }

                if(!this.practicePulseSettings.quickNotesOptions) {                
                    this.practicePulseSettings.quickNotesOptions = this.quickNotesOptions;
                }

                // now load the provider colors for any employee that are providers
                if(this.providers) {
                    let p;
                    for(const emp of this.activeEmployees) {
                        if(this.practicePulseSettings.providerSettings[emp.EmployeeID] && this.practicePulseSettings.providerSettings[emp.EmployeeID].color === '') {
                            p = this.getProviderFromEmployeeID(emp.EmployeeID);
                            //console.log('getProviderFromEmployeeID, got provider', p);
                            if(p) {
                                //console.log('found provider', p);                        
                                this.practicePulseSettings.providerSettings[emp.EmployeeID].color = this.numToHex(p.ProvColor);                        
                            }
                        }
                    }
                }                
                
            }).catch(error => {
                this.overlayshow = !this.overlayshow;
                console.log(error);    
                setErrored2(true);
                setLoading2(false);
                setMessage2('Error');
            });
        },

        codeWithDesc({ ProcCode, Descript }){
            return `${ProcCode} — [${Descript}]`;
        },

        removeCode(index, option) {            
            let removeIdx = this.practicePulseSettings.procCodesToWikiMappings[index].procCodes.findIndex(each => {
                return each.ProcCode === option.ProcCode;
            });
            this.practicePulseSettings.procCodesToWikiMappings[index].procCodes.splice(removeIdx, 1);
        },

        doCancel() {
            /*            
            console.log('this.practicePulseSettings', this.practicePulseSettings);
            for(let i=0; i< this.practicePulseSettings.allOps.length; i++) {
                let op = this.practicePulseSettings.allOps[i];
                if(op.addedFromTeamio) {
                    Vue.delete(this.practicePulseSettings.allOps, i);
                    Vue.delete(this.practicePulseSettings.ops, op.OperatoryNum);
                    this.$forceUpdate();
                }
            }
            console.log('after deleting', this.practicePulseSettings);
            */
            this.setErrored2(false);
            this.setMessage2('');
            this.closeSlideOutPanel();            
        },

        selectEmp(value, index) {
            console.log(value, index);
            this.$nextTick(() => Vue.set(this.$refs.emp[index], 'value', value));
        },

        setVal(value, ref, index) {
            this.$nextTick(() => Vue.set(this.$refs[ref][index], 'value', value));
        },

        playSound(soundObj) {      
            //console.log('sound obj', soundObj);
            let name = soundObj.name;    
            let url = soundObj.url;
            let fileType = soundObj.type;  

            var soundB64 = localStorage.getItem(`${name}_base64`);
            //console.log(soundB64);

            if(soundB64 !== null) {
                //console.log('now playing', name, url);
                var context = new AudioContext();
                //console.log('audio context state', context.state);
                if(context.state === 'suspended') {                            
                    alert('Please enable Sound for this site first. You will not be able to hear any sound notifications otherwise.');
                } else {                
                    context.resume().then(() => {
                        this._playFile(name);
                    });
                }                
            } else {
                let _this = this;
                //console.log('now downloading', name, url);
                var xhr = new XMLHttpRequest();
                xhr.open('GET', url, true);
                xhr.responseType = 'blob';
                xhr.onload = function(e) {
                    if (this.status == 200) {            
                        var blob = new Blob([this.response], {
                            type: fileType
                        });

                        var reader = new window.FileReader();
                        reader.readAsDataURL(blob);
                        reader.onloadend = function() {
                            var urlB64 = reader.result;
                            var myStorage = localStorage.setItem(`${name}_base64`, urlB64);
                            var context = new AudioContext();
                            //console.log('audio context state', context.state);
                            if(context.state === 'suspended') {                            
                                alert('Please enable Sound for this site first. You will not be able to hear any sound notifications otherwise.');
                            } else {                
                                context.resume().then(() => {
                                    _this._playFile(name);
                                });
                            }
                        }
                    }
                };
                xhr.send();
            }                        
        },

        _playFile(name) {
            var soundB64 = localStorage.getItem(`${name}_base64`);
            //console.log('soundB64', soundB64);
            var sound = new Audio(soundB64);
            let promise = sound.play();
            if (promise !== undefined) {
            promise.then(_ => {
                // Autoplay started!
                //console.log('played sound');
            }).catch(error => {
                // Autoplay was prevented.
                // Show a "Play" button so that user can start playback.
                console.error(error);
            });
            }
        },

        providerCustomLabel({FName, MI, LName, Suffix, Abbr}) {
            return `${FName} ${MI} ${LName} ${Suffix} (${Abbr})`;
        },

        showModal(name) {            
            this.$refs[name].show();
        },

        hideModal(ref) {
            this.$refs[ref].hide()
        },

        addAlert() {
            let alertObject;

            if(this.practicePulseSettings) {                
                alertObject = {
                    provider: null,
                    alertText: ''
                }

                if(this.practicePulseSettings.customAlerts) {
                    this.practicePulseSettings.customAlerts.push(alertObject);
                } else {                
                    let alerts = [];
                    alerts.push(alertObject);
                    Vue.set(this.practicePulseSettings, 'customAlerts', alerts);
                }
            }
        },

        addQuickNotes() {
            let main = {};
            let key = Date.now() + '';
            //console.log('key', key);

            if(this.practicePulseSettings.quickNotesOptions2)
                main = this.practicePulseSettings.quickNotesOptions2;

            main[key] = {
                title: 'Category',
                val: [
                    {
                        name: 'sub-heading',
                        checked: false,
                        autoNotes: 'Your Auto Notes goes here'
                    }
                ]
            }

            main = _.pick(main, Object.keys(main).sort());
            Vue.set(this.practicePulseSettings, 'quickNotesOptions2', main);
            this.$forceUpdate();
        },

        addQuickNoteSubHeading(key) {
            let main;

            if(this.practicePulseSettings.quickNotesOptions2)
                main = this.practicePulseSettings.quickNotesOptions2;

            let temp = {
                        name: 'sub-heading',
                        checked: false,
                        autoNotes: 'Your Auto Notes goes here'
                    };

            main[key].val.push(temp);
            Vue.set(this.practicePulseSettings, 'quickNotesOptions2', main);
            this.$forceUpdate();        
        },

        async addOp() {
            if(this.practicePulseSettings) {
                let opNum = Date.now();

                let opObj = {
                    OpName: '',
                    OperatoryNum: opNum,
                    addedFromTeamio: true
                }

                //console.log('opObj', opObj);

                if(this.practicePulseSettings.ops && this.practicePulseSettings.allOps) {
                    this.practicePulseSettings.ops[opNum] = [];
                    this.practicePulseSettings.allOps.push(opObj);
                    this.$forceUpdate();
                }
            } else {
                throw new Error("practice-pulse settings not available");                
            }
        },

        delAlert() {            
            this.practicePulseSettings.customAlerts.splice(this.selectedAlert, 1);            
            this.hideModal('delete');
        },

        delOp() {
            console.log('this.selectedOpNum', this.selectedOpNum);
            Vue.delete(this.practicePulseSettings.ops, this.selectedOpNum);
            Vue.delete(this.practicePulseSettings.allOps, this.selectedOpIndex);
            this.hideModal('deleteOp');
        },

        delQuickNoteCat(key) {
            let main = this.practicePulseSettings.quickNotesOptions2;
            delete main[key];
            Vue.set(this.practicePulseSettings, 'quickNotesOptions2', main);
            this.$forceUpdate();
        },

        delQuickNoteSubcat(key, index) {
            let main = this.practicePulseSettings.quickNotesOptions2;
            main[key].val.splice(index,1);
            Vue.set(this.practicePulseSettings, 'quickNotesOptions2', main);
            this.$forceUpdate();
        },

        async closeSlideOutPanel() {          
          this.$emit('closePanel', this.practicePulseSettings);
        },

        async save() {
            if(this.practicePulseSettings.elapsedTimeAlert === 'TRUE' && !this.practicePulseSettings.elapsedTime) {
                this.setErrored2(true);
                this.setMessage2("Please enter a value for how long to wait for visual alert?")                
                return false;
            }

            // Iterate over ops from practice pulse settings, but take the members from pp.ops            

            let temp = {}

            //console.log('this.pp.ops', this.pp.ops);

            for(const op in this.practicePulseSettings.ops) {
                if(this.pp.ops[op]) {
                    temp[op] = this.pp.ops[op];
                } else {
                    temp[op] = [];
                }
            }

            let _checkin = {};

            for(const _item of this.pp.ops['checkin']) {
                //console.log('item', _item);
                _checkin[_item.patNum] = _item;
            }

            temp.checkin = _checkin;
            temp.checkout = this.pp.ops.checkout;

            let payload = {
                ppFBID : this.siteID,                
                ops: temp,
                settings: this.practicePulseSettings
            }
            //console.log('payload to update ops in FB', payload);

            try {
                this.overlayshow = !this.overlayshow;
                await this.updateOps(payload); 
                //await this.savePracticePulseSettings(payload2);
                this.$toast.success("Settings saved successfully!");
                this.$emit('closePanel', this.practicePulseSettings);
            } catch(err) {
                this.overlayshow = !this.overlayshow;
                console.log(err);
            }            
        },

        addMoreProcCodeToWikiMapping() {
            let temp = this.practicePulseSettings.procCodesToWikiMappings || [];
            temp.push({
                procCodes: [],
                wikiPage: []
            });
            Vue.set(this.practicePulseSettings, 'procCodesToWikiMappings', temp);
        },

        deleteProcCodeToWikiMapping() {
            this.hideModal('delProcCodeMapping');
            this.practicePulseSettings.procCodesToWikiMappings.splice(this.selectedMappingRowIndex,1);
            //Vue.set(this.practicePulseSettings, 'quickNotesOptions2', main);
            this.$forceUpdate();            
        }        
    },

    computed: {
        ...mapState({            
            procCodes: state => state.customer.procCodes
        }),

        alertSounds() {
            // One list for this page and the Practice Ops Settings panel (src/utils/practiceOps/alertSounds.js).
            return alertSoundList(this.isDev);
        }
    }
}

</script>

<style lang='scss' scoped>
$brand-green: #3abf94;
$border-color: #e9ecef;
$text-primary: #2c3e50;
$text-secondary: #6c757d;
$bg-light: #f8f9fa;

.configure-practice-pulse {
    background: #fff;
    min-height: 100vh;
}

// Header Styles
.config-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: linear-gradient(135deg, $brand-green 0%, darken($brand-green, 8%) 100%);
    border-bottom: 1px solid $border-color;
    position: sticky;
    top: 0;
    z-index: 100;

    .header-content {
        .header-title {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #fff;
        }
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 10px;

        .btn-save {
            background: #fff !important;
            color: $brand-green !important;
            border: none !important;
            font-weight: 600;
            padding: 8px 20px;
            font-size: 0.9rem;

            &:hover {
                background: #f1f5f9 !important;
                color: darken($brand-green, 5%) !important;
            }

            &:focus {
                box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3) !important;
            }
        }

        .btn-cancel {
            background: transparent;
            color: #fff;
            border: 1px solid rgba(255, 255, 255, 0.5);

            &:hover {
                background: rgba(255, 255, 255, 0.1);
                border-color: #fff;
            }
        }

        .btn-close-panel {
            background: transparent;
            border: none;
            color: #fff;
            font-size: 1.25rem;
            padding: 4px 8px;
            cursor: pointer;
            opacity: 0.8;

            &:hover {
                opacity: 1;
            }
        }
    }
}

// Content Container
.config-content {
    padding: 20px;
}

// Section Styles
.config-section {
    background: #fff;
    border: 1px solid $border-color;
    border-radius: 8px;
    margin-bottom: 20px;
    // ⚠️ NO `overflow: hidden` HERE. It used to be, purely to clip the header's
    // background to the rounded corner — but it also clipped every vue-multiselect
    // menu opened inside the section. The Status Settings section is only ~40px tall
    // below its selects, so the 300px option list was sliced to a sliver: reported by
    // Nilay 2026-08-19 as "dropdowns don't show options". The header now rounds its
    // own top corners (7px = the 8px outer radius minus the 1px border), which is
    // pixel-identical to what the clip produced, without trapping descendants.

    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: $bg-light;
        border-bottom: 1px solid $border-color;
        border-radius: 7px 7px 0 0;

        .section-title {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
            color: $text-primary;
            display: flex;
            align-items: center;
            gap: 10px;

            .section-icon {
                color: $brand-green;
                font-size: 1.1rem;
            }
        }

        .btn-add-new {
            background: $brand-green;
            border: none;
            font-weight: 500;
            font-size: 0.85rem;

            &:hover {
                background: darken($brand-green, 8%);
            }
        }
    }

    .section-body {
        padding: 20px;
    }
}

// Form Grid
.form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
}

.form-field {
    .field-label {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: $text-primary;
        margin-bottom: 8px;
    }
}

// Checkbox Styles
.checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.checkbox-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: $bg-light;
    border-radius: 6px;
    cursor: pointer;
    margin: 0;
    transition: background 0.2s;

    &:hover {
        background: darken($bg-light, 3%);
    }

    .checkbox-label {
        font-size: 0.9rem;
        color: $text-primary;
        line-height: 1.5;
    }
}

.checkbox-item-complex {
    .checkbox-item {
        margin-bottom: 0;
        border-radius: 6px 6px 0 0;
    }

    .checkbox-sub-content {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        padding-left: 44px;
        background: darken($bg-light, 2%);
        border-radius: 0 0 6px 6px;
        flex-wrap: wrap;

        .elapsed-input {
            width: 80px;
            text-align: center;
        }

        .checkbox-label-continued {
            font-size: 0.9rem;
            color: $text-primary;
            flex: 1;
            min-width: 200px;
        }
    }
}

// Data Table Styles
.data-table {
    .table {
        margin-bottom: 0;
        border: 1px solid $border-color;
        border-radius: 6px;
        // Same reason as .config-section: `overflow: hidden` here clipped the
        // employee/sound vue-multiselect menus opened inside these rows. Corners are
        // rounded on the edge cells instead (5px = 6px outer minus the 1px border).

        thead {
            background: $bg-light;

            th {
                font-size: 0.85rem;
                font-weight: 600;
                color: $text-primary;
                border-bottom: 1px solid $border-color;
                padding: 12px 16px;

                &:first-child { border-top-left-radius: 5px; }
                &:last-child { border-top-right-radius: 5px; }
            }

            .th-actions {
                width: 60px;
            }
        }

        tbody {
            tr {
                &:hover {
                    background: rgba($brand-green, 0.03);
                }
            }

            td {
                padding: 12px 16px;
                vertical-align: middle;
                border-bottom: 1px solid $border-color;
            }

            tr:last-child td {
                border-bottom: none;

                // Bottom corners were previously produced by the parent's clip; the
                // row hover background would otherwise square them off.
                &:first-child { border-bottom-left-radius: 5px; }
                &:last-child { border-bottom-right-radius: 5px; }
            }
        }
    }

    .table-input {
        border: 1px solid $border-color;
        border-radius: 4px;
        padding: 8px 12px;

        &:focus {
            border-color: $brand-green;
            box-shadow: 0 0 0 2px rgba($brand-green, 0.1);
        }
    }

    .td-actions {
        text-align: center;
    }

    .btn-delete {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 4px;
        color: #dc3545;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background: rgba(#dc3545, 0.1);
        }
    }
}

// Sound Field
.sound-field {
    display: flex;
    align-items: center;
    gap: 10px;

    .multiselect {
        flex: 1;
    }

    .btn-play-sound {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: $brand-green;
        color: #fff;
        border: none;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background: darken($brand-green, 8%);
            transform: scale(1.05);
        }
    }
}

// Color Input
.color-input {
    width: 60px;
    height: 36px;
    padding: 2px;
    border: 1px solid $border-color;
    border-radius: 4px;
    cursor: pointer;
}

// Post-op Time Setting
.postop-time-setting {
    margin-bottom: 20px;
    padding: 16px;
    background: $bg-light;
    border-radius: 6px;

    .field-label {
        display: block;
        font-size: 0.9rem;
        color: $text-primary;
        margin-bottom: 12px;
    }

    .time-picker {
        max-width: 200px;
    }
}

// Quick Notes Styles
.quick-notes-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.quick-note-category {
    border: 1px solid $border-color;
    border-radius: 8px;
    overflow: hidden;

    .category-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: $bg-light;
        border-bottom: 1px solid $border-color;

        .btn-delete-cat {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border: none;
            background: transparent;
            color: #dc3545;
            border-radius: 4px;
            cursor: pointer;

            &:hover {
                background: rgba(#dc3545, 0.1);
            }
        }

        .category-title-input {
            flex: 1;
            font-weight: 600;
            border: 1px solid transparent;
            background: transparent;

            &:focus {
                border-color: $brand-green;
                background: #fff;
            }
        }

        .btn-add-sub {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border: none;
            background: $brand-green;
            color: #fff;
            border-radius: 4px;
            cursor: pointer;

            &:hover {
                background: darken($brand-green, 8%);
            }
        }
    }

    .category-items {
        padding: 12px 16px;
        padding-left: 56px;
    }

    .category-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 0;
        border-bottom: 1px solid $border-color;

        &:last-child {
            border-bottom: none;
        }

        .btn-delete-item {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border: none;
            background: transparent;
            color: #dc3545;
            border-radius: 4px;
            cursor: pointer;
            margin-top: 6px;

            &:hover {
                background: rgba(#dc3545, 0.1);
            }
        }

        .item-inputs {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            .item-name-input,
            .item-notes-input {
                width: 100%;
                border: 1px solid $border-color;
                border-radius: 4px;
                padding: 8px 12px;

                &:focus {
                    border-color: $brand-green;
                }
            }

            .item-notes-input {
                font-size: 0.85rem;
                color: $text-secondary;
            }
        }
    }
}

// Add More Link
.btn-add-more {
    display: inline-flex;
    align-items: center;
    margin-top: 12px;
    padding: 8px 16px;
    color: $brand-green;
    font-weight: 500;
    text-decoration: none;
    border-radius: 4px;

    &:hover {
        background: rgba($brand-green, 0.1);
        text-decoration: none;
    }
}

// Mobile Display
.mobile-display {
    display: none;

    @media (max-width: 768px) {
        display: block;
        font-weight: 600;
        margin-bottom: 4px;
        font-size: 0.85rem;
    }
}

// Multiselect Override
.multiselect {
    max-width: 100%;
}

// Legacy class support
.settingsDisabled {
    background-color: #cccccc;
    color: #666666;
}

</style>